import { useBehavior, useTriggerAction } from '@libs/hooks';
import { ActionActionTypeEnum, TWidgetProps } from '@libs/types';
import { ConditionalRender } from '@libs/utils';
import React, { Activity, useRef } from 'react';
import { TWebViewLayout } from '../../types/data';
import { TWebViewLayoutOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import styles from './webViewLayout.module.css';

const getFinalSize = (value?: string, isWidth?: boolean): string => {
    if (value) {
        return value.endsWith('%') ? value.replace('%', isWidth ? 'vw' : 'vh') : `${value}px`;
    }
    return '100%';
};

const isValidUrl = (url: string) => {
    try {
        const newUrl = new URL(url);
        return newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
    } catch {
        return false;
    }
};

export const WebViewLayout: React.FC<TWidgetProps<TWebViewLayout, TWebViewLayoutOption>> = ({ widget }) => {
    const { triggerActionsByNameAndType } = useTriggerAction(widget);
    const { isHidden } = useBehavior(widget);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const { value, redirects } = widget.data ?? {};
    const { margin, width, height } = widget.options ?? {};

    const checkIframeNavigation = () => {
        try {
            if (Array.isArray(redirects) && redirects.length > 0 && iframeRef.current && iframeRef.current.contentWindow) {
                const currentURL = iframeRef.current.contentWindow.location.href;
                const redirect = redirects.find((r) => currentURL.includes(r.url));
                if (redirect) {
                    triggerActionsByNameAndType(ActionActionTypeEnum.onRedirect, redirect.actionName).catch((e) => console.error(e));
                }
            }
        } catch (e) {
            console.warn('Cannot access iframe URL due to cross-origin restrictions.', e);
        }
    };

    const src = value && isValidUrl(value) ? value : undefined;

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <ConditionalRender condition={!!src}>
                <iframe
                    ref={iframeRef}
                    data-test={widget.uid}
                    src={src}
                    title="WebViewLayout"
                    onLoad={checkIframeNavigation}
                    className={styles.iframe}
                    style={getInlineStyles({ margin, variables: { 'iframe-height': getFinalSize(height), 'iframe-width': getFinalSize(width, true) } })}
                />
            </ConditionalRender>
        </Activity>
    );
};
